<template>
    <div style="min-height: calc(100vh - 158px);" class="card">
        <PageHeader
            :showBtn="false"
            title="Ecommerce Sync Management"
        />

        <div class="filter-section">
            <div class="filter-bar">
                <div class="filter-field">
                    <label class="filter-label">Action</label>
                    <v-select
                        placeholder="Select Action"
                        v-model="selectedAction"
                        :options="actionList"
                        :reduce="item => item.value"
                        label="name"
                    />
                </div>

                <div class="filter-field">
                    <label class="filter-label">Integration</label>
                    <v-select
                        placeholder="Select Integration"
                        v-model="selectedIntegrationId"
                        :options="availableIntegrations"
                        :reduce="item => item.id"
                        label="name"
                    />
                </div>

                <button
                    @click="onClickProcess"
                    :disabled="! canProcess"
                    class="btn process-btn"
                >Process</button>
            </div>
        </div>

        <div class="table-section">
            <div class="table-responsive">
                <table class="table sync-table">
                    <thead>
                    <tr>
                        <th style="width: 40px"></th>
                        <th>Integration</th>
                        <th>Sync Type</th>
                        <th>Direction</th>
                        <th>Started At</th>
                        <th>Result</th>
                        <th class="text-center">Status</th>
                    </tr>
                    </thead>
                    <tbody>
                    <template v-for="(run, i) in tableItems" :key="i">
                        <tr :class="rowClass(run)" @click="onClickExpand(run)">
                            <td class="text-center">
                                <i v-if="run.expandable" :class="chevronClass(run)" class="chevron"></i>
                                <span v-if="! run.expandable" class="text-muted">&mdash;</span>
                            </td>
                            <td class="fw-medium">{{ integrationName(run) }}</td>
                            <td>{{ syncTypeLabel(run.sync_type) }}</td>
                            <td class="text-capitalize">{{ run.direction }}</td>
                            <td class="text-nowrap">{{ formatDateTime(run.created_at) }}</td>
                            <td class="result-cell">{{ runSummaryText(run) }}</td>
                            <td class="text-center text-capitalize">
                                <span :class="statusBadgeClass(run.status)">{{ run.status }}</span>
                            </td>
                        </tr>
                        <tr v-if="isExpanded(run)" class="child-row">
                            <td colspan="7" class="p-0">
                                <div class="child-panel">
                                    <p v-if="childLoading" class="child-note">Loading…</p>
                                    <table v-if="! childLoading" class="table sync-table child-table mb-0">
                                        <thead>
                                        <tr>
                                            <th>Reference</th>
                                            <th>Message</th>
                                            <th class="text-center">Status</th>
                                            <th>Time</th>
                                            <th v-if="hasRetryableItem(run)" class="text-center">Action</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr v-for="(item, k) in childRows(run)" :key="k">
                                            <td class="fw-medium">{{ item.external_reference }}</td>
                                            <td>{{ itemMessage(item) }}</td>
                                            <td class="text-center text-capitalize">
                                                <span :class="statusBadgeClass(item.status)">{{ item.status }}</span>
                                            </td>
                                            <td class="text-nowrap">{{ formatDateTime(item.created_at) }}</td>
                                            <td v-if="hasRetryableItem(run)" class="text-center">
                                                <button
                                                    v-if="canRetryItem(item)"
                                                    @click.stop="onClickRetryItem(item)"
                                                    class="btn retry-btn"
                                                >Retry</button>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                    <p v-if="! childLoading && childRows(run).length < 1" class="child-note">
                                        This run wrote no item rows
                                    </p>
                                </div>
                            </td>
                        </tr>
                    </template>
                    </tbody>
                </table>
                <p v-if="tableItems.length < 1" class="empty-note">No data</p>
            </div>
        </div>

        <div class="pagination-section">
            <Pagination :offset="params.offset" @onPageChange="onPageChange" ref="pagination"/>
        </div>

        <Loader v-if="loading"/>

    </div>
</template>

<script setup>
import PageHeader from '@/components/atom/PageHeader';
import { computed, inject, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import handleEcommerce from '@/services/modules/ecommerce';
import Pagination from '@/components/atom/Pagination'
import format from 'date-fns/format'

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false)
const params = ref({
    offset: 20,
});
const tableItems = ref([]);
const integrations = ref([]);
const selectedAction = ref('');
const selectedIntegrationId = ref('');

const actionList = ref([])

const expandedRunId = ref(0)
const childItems = ref({})
const childLoading = ref(false)

const {
    fetchSyncIntegrations,
    triggerIntegrationSyncAction,
    fetchSyncLogs,
    fetchSyncRuns,
    retrySyncItem,
} = handleEcommerce()

const syncTypeLabels = {
    'product-pull': 'Product Pull',
    'product-push': 'Product Push',
    'inventory-push': 'Inventory Push',
    'order-pull': 'Order Pull',
    'order-status-push': 'Order Status Push',
}

const summaryKeyLabels = {
    total: 'Total',
    succeeded: 'Succeeded',
    failed: 'Failed',
    staged: 'Staged',
    skipped: 'Skipped',
    booked: 'Booked',
    duplicate: 'Duplicate',
    rejected: 'Rejected',
    unclaimed: 'Unclaimed',
    deliverable: 'Deliverable',
    queued: 'Queued',
    blocked: 'Blocked',
}

const availableIntegrations = computed(() => {
    const matched = actionList.value.find(item => item.value === selectedAction.value)

    if (! matched) return []

    const grantKey = matched.grant_key

    if (! grantKey) return []

    return integrations.value
        .filter(item => item[grantKey])
        .map(item => ({ id: item.id, name: item.platform + ' - ' + item.endpoint }))
})

const canProcess = computed(() => selectedAction.value && selectedIntegrationId.value)

const statusBadgeClass = (status) => {
    if (status === 'success') return 'status-chip is-success'
    if (status === 'failed') return 'status-chip is-failed'
    if (status === 'partial') return 'status-chip is-warning'
    if (status === 'throttled') return 'status-chip is-warning'
    if (status === 'duplicate') return 'status-chip is-info'
    if (status === 'skipped') return 'status-chip is-neutral'
    return 'status-chip is-neutral'
}

const syncTypeLabel = (syncType) => {
    if (! syncType) return ''
    if (! syncTypeLabels[syncType]) return syncType
    return syncTypeLabels[syncType]
}

const integrationName = (run) => {
    if (! run.platform) return run.api_integration_id
    if (! run.endpoint) return run.platform
    return run.platform + ' - ' + run.endpoint
}

const formatDateTime = (value) => {
    if (! value) return ''
    const parsed = new Date(value)
    if (isNaN(parsed.getTime())) return value
    return format(parsed, 'dd MMM yyyy, hh:mm a')
}

const runPayload = (run) => {
    if (! run.response_payload) return null
    if (typeof run.response_payload !== 'string') return run.response_payload
    try {
        return JSON.parse(run.response_payload)
    } catch (err) {
        return null
    }
}

const summaryKeyLabel = (key) => {
    if (! summaryKeyLabels[key]) return key
    return summaryKeyLabels[key]
}

const runSummaryText = (run) => {
    const payload = runPayload(run)
    if (! payload) return itemMessage(run)
    const parts = Object.keys(payload)
        .filter(key => payload[key] > 0 || key === 'total')
        .map(key => payload[key] + ' ' + summaryKeyLabel(key))
    if (parts.length < 1) return itemMessage(run)
    return parts.join(' · ')
}

const chevronClass = (run) => {
    if (isExpanded(run)) return 'fas fa-chevron-down'
    return 'fas fa-chevron-right'
}

const rowClass = (run) => {
    if (! run.expandable) return ''
    return 'cursor-pointer'
}

const isExpanded = (run) => expandedRunId.value === run.id

const childRows = (run) => {
    if (! childItems.value[run.id]) return []
    return childItems.value[run.id]
}

const onClickExpand = async (run) => {
    if (! run.expandable) return
    if (isExpanded(run)) {
        expandedRunId.value = 0
        return
    }
    expandedRunId.value = run.id
    if (childItems.value[run.id]) return
    await getRunItems(run)
}

const getRunItems = async (run) => {
    let query = `?company_id=${route.params.companyId}`
    query += '&sync_type=' + run.item_sync_type
    query += '&direction=' + run.item_direction
    query += '&to_id=' + run.id
    query += '&per_page=100'
    if (run.prev_summary_id > 0) query += '&from_id=' + run.prev_summary_id

    childLoading.value = true
    await fetchSyncLogs(query).then(res => {
        if (! res.status) return
        childItems.value[run.id] = res.data.data
    }).finally(() => {
        childLoading.value = false
    })
}

const itemMessage = (item) => {
    if (! item) return ''
    if (! item.error_message) return ''
    return item.error_message
}

const canRetryItem = (item) => {
    if (item.status !== 'failed') return false
    return item.reference_type !== 'batch-summary'
}

const hasRetryableItem = (run) => {
    return childRows(run).some(item => canRetryItem(item))
}

const setPaginationData = (data) => {
    store.commit('setPaginationData', data)
};

const onPageChange = async (page) => {
    let routeQuery = Object.assign({}, route.query)
    routeQuery.page = page
    await router.push({ path: route.path, query: routeQuery })
    await getSyncLogs();
}

const setPagination = (data) => {
    setPaginationData({
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    })
}

const getSyncLogs = async () => {
    let query = `?company_id=${route.params.companyId}`;
    if (route.query.page) query += '&page=' + route.query.page;
    query += '&per_page=' + params.value.offset
    loading.value = true
    tableItems.value = [];
    expandedRunId.value = 0
    childItems.value = {}

    await fetchSyncRuns(query).then(res => {
        if (! res.status) {
            store.commit("resetPagination")
            return
        }
        tableItems.value = res.data.data
        setPagination(res.data)
    }).finally(() => {
        loading.value = false
    })
}

const getIntegrations = async () => {
    let query = `?company_id=${route.params.companyId}`;
    await fetchSyncIntegrations(query).then(res => {
        if (! res.status) {
            integrations.value = [];
            actionList.value = [];
            return
        }
        integrations.value = res.data || [];
        actionList.value = res.actions || [];
    })
}

const onClickProcess = async () => {
    if (! canProcess.value) return

    loading.value = true
    const data = { company_id: route.params.companyId, action: selectedAction.value }
    await triggerIntegrationSyncAction(selectedIntegrationId.value, data).then(res => {
        if (! res.status) {
            showError(res.message)
            return
        }
        showSuccess(res.message)
        getSyncLogs()
    }).finally(() => {
        loading.value = false
    })
}

const onClickRetryItem = async (item) => {
    if (! confirm('Are you sure you want to retry this item?')) return
    const data = { company_id: route.params.companyId }
    const openRun = tableItems.value.find(run => run.id === expandedRunId.value)

    loading.value = true
    await retrySyncItem(item.id, data).then(res => {
        if (! res.status) {
            showError(res.message)
            return
        }
        showSuccess(res.message)
        refreshAfterRetry(openRun)
    }).finally(() => {
        loading.value = false
    })
}

const refreshAfterRetry = (openRun) => {
    if (! openRun) return getSyncLogs()
    delete childItems.value[openRun.id]
    return getRunItems(openRun)
}

onMounted(() => {
    Promise.all([
        getIntegrations(),
        getSyncLogs(),
    ])
})

</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}
.filter-section {
    padding: 16px 20px 0;
}
.filter-bar {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
}
.filter-field {
    display: flex;
    flex-direction: column;
    min-width: 240px;
}
.filter-label {
    display: block;
    margin-bottom: 4px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #101828;
}
.filter-field :deep(.vs__dropdown-toggle) {
    border-radius: 8px;
    border-color: #e4e7ec;
}
.filter-field :deep(.vs__selected) {
    color: #101828;
}
.process-btn {
    height: 38px;
    min-width: 96px;
    border-radius: 8px;
    background: #7367f0;
    border: 1px solid #7367f0;
    color: #fff;
    font-size: 13px;
    font-weight: 500;
}
.process-btn:hover:not(:disabled) {
    background: #5e50ee;
    border-color: #5e50ee;
    color: #fff;
}
.process-btn:disabled {
    opacity: .5;
    cursor: not-allowed;
    color: #fff;
}
.table-section {
    padding: 16px 20px 0;
}
.pagination-section {
    padding: 12px 20px 16px;
}
.sync-table {
    margin-bottom: 0;
}
.sync-table thead th {
    background: #f9fafb;
    border-bottom: 1px solid #eaecf0;
    padding: 10px 12px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #101828;
    white-space: nowrap;
}
.sync-table tbody td {
    padding: 12px;
    border-bottom: 1px solid #f2f4f7;
    font-size: 13px;
    color: #101828;
    vertical-align: middle;
}
.sync-table tbody tr:hover {
    background: #fcfcfd;
}
.fw-medium {
    font-weight: 500;
    color: #101828;
}
.result-cell {
    color: #101828;
}
.chevron {
    font-size: 11px;
    color: #101828;
}
.child-row:hover {
    background: transparent;
}
.child-panel {
    padding: 12px 16px;
    background: #f9fafb;
    border-bottom: 1px solid #eaecf0;
}
.child-table {
    background: #fff;
    border: 1px solid #eaecf0;
    border-radius: 8px;
    overflow: hidden;
}
.child-table thead th {
    background: #fcfcfd;
}
.child-table tbody tr:last-child td {
    border-bottom: 0;
}
.child-note {
    margin: 0;
    padding: 8px 0;
    text-align: center;
    font-size: 13px;
    color: #101828;
}
.retry-btn {
    height: 28px;
    padding: 0 12px;
    border-radius: 6px;
    border: 1px solid #fedf89;
    background: #fffaeb;
    color: #b54708;
    font-size: 12px;
    font-weight: 500;
}
.retry-btn:hover {
    background: #fef0c7;
    color: #93370d;
}
.status-chip {
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 10px;
    border-radius: 11px;
    border: 1px solid transparent;
    font-size: 12px;
    font-weight: 500;
}
.status-chip.is-success {
    background: #ecfdf3;
    border-color: #abefc6;
    color: #067647;
}
.status-chip.is-failed {
    background: #fef3f2;
    border-color: #fecdca;
    color: #b42318;
}
.status-chip.is-warning {
    background: #fffaeb;
    border-color: #fedf89;
    color: #b54708;
}
.status-chip.is-info {
    background: #eff8ff;
    border-color: #b2ddff;
    color: #175cd3;
}
.status-chip.is-neutral {
    background: #f9fafb;
    border-color: #e4e7ec;
    color: #101828;
}
.text-muted {
    color: #101828 !important;
}
.empty-note {
    padding: 32px 0;
    text-align: center;
    font-size: 13px;
    color: #101828;
}
</style>
